import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import { rupee } from "../../lib/format";
import { useCart } from "../../context/CartContext";
import { VegBadge } from "./ItemCard";

export function AddItemModal({ item, onClose }) {
  const { addItem } = useCart();
  const [variant, setVariant] = useState(item?.variants?.length ? item.variants[0] : null);
  const [addons, setAddons] = useState([]);
  if (!item) return null;

  const toggleAddon = (a) => {
    setAddons((prev) =>
      prev.find((x) => x.name === a.name) ? prev.filter((x) => x.name !== a.name) : [...prev, a]);
  };

  const total = (item.price + (variant?.price || 0) + addons.reduce((s, a) => s + a.price, 0));

  const confirm = () => {
    addItem(item, variant, addons, 1);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="add-item-modal">
      <div className="w-full max-w-md card-surface rounded-t-3xl sm:rounded-3xl overflow-hidden animate-fadeup"
           onClick={(e) => e.stopPropagation()}>
        <div className="relative h-40">
          <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
          <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 rounded-full glass flex items-center justify-center"
                  data-testid="close-modal"><X size={16} /></button>
        </div>
        <div className="p-5 max-h-[50vh] overflow-y-auto">
          <div className="flex items-center gap-2">
            <VegBadge type={item.food_type} />
            <h3 className="font-display font-bold text-lg">{item.name}</h3>
          </div>
          <p className="text-xs text-slate-400 mt-1">{item.description}</p>

          {item.variants?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2">Choose size</div>
              {item.variants.map((v) => (
                <label key={v.name}
                  className={`flex items-center justify-between p-3 rounded-xl mb-2 cursor-pointer border transition-colors ${variant?.name === v.name ? "border-amber-500 bg-amber-500/10" : "hairline"}`}
                  data-testid={`variant-${v.name}`}>
                  <span className="text-sm">{v.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-300">{v.price > 0 ? `+${rupee(v.price)}` : "Base"}</span>
                    <input type="radio" checked={variant?.name === v.name} onChange={() => setVariant(v)} className="accent-amber-500" />
                  </div>
                </label>
              ))}
            </div>
          )}

          {item.addons?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2">Add-ons</div>
              {item.addons.map((a) => (
                <label key={a.name}
                  className={`flex items-center justify-between p-3 rounded-xl mb-2 cursor-pointer border transition-colors ${addons.find((x) => x.name === a.name) ? "border-amber-500 bg-amber-500/10" : "hairline"}`}
                  data-testid={`addon-${a.name}`}>
                  <span className="text-sm">{a.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-300">+{rupee(a.price)}</span>
                    <input type="checkbox" checked={!!addons.find((x) => x.name === a.name)} onChange={() => toggleAddon(a)} className="accent-amber-500" />
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t hairline">
          <button onClick={confirm} className="gold-btn w-full rounded-xl py-3 flex items-center justify-center gap-2"
                  data-testid="confirm-add-item">
            <Plus size={18} /> Add item · {rupee(total)}
          </button>
        </div>
      </div>
    </div>
  );
}
